// Boot: the API (the server, or the demo in memory), language, theme and clock; then the app.
// Demo and screenshot flags: ?demo · &lang=de|en · &theme=light|dark · &at=2026-09-25T14:36 (frozen clock) · &signedout
// · &guest (the guest mode, kept in memory only)
import { createRoot } from 'react-dom/client';
import '../../design/tokens.css';
import '../../design/components/bundle.css';
import './styles/app.css';
import { HttpApi, type Api } from './api/client.ts';
import { DemoApi, demoNow } from './api/demo.ts';
import { GuestApi, guestMode } from './api/guest.ts';
import { App } from './app/App.tsx';
import { browserTimeZone, parseLocal } from './lib/calendar.ts';
import { makeFormats } from './lib/format.ts';
import { preferredLang, translator, type Lang } from './lib/i18n.ts';
import { applyTheme, langChoice, themeChoice } from './lib/prefs.ts';
import { StoreProvider } from './store/hooks.tsx';
import { Store } from './store/store.ts';
import { parseRoute } from './lib/router.ts';
import { AuthBoundary } from './app/auth.tsx';

const params = new URLSearchParams(window.location.search);
const timeZone = browserTimeZone();

const themeParam = params.get('theme');
applyTheme(themeParam === 'light' || themeParam === 'dark' ? themeParam : themeChoice());

const langParam = params.get('lang');
const chosen = langChoice();
const lang: Lang = langParam === 'de' || langParam === 'en' ? langParam : chosen === 'system' ? preferredLang() : chosen;

const demo = params.has('demo');
let clock: () => number = () => Date.now();
if (demo) {
  // The demo's day is today at 14:36 (the design system's reference); `at` freezes a moment for screenshots.
  const frozen = params.get('at') ? parseLocal(params.get('at') ?? '', timeZone) : null;
  const start = demoNow(Date.now(), timeZone);
  const booted = Date.now();
  clock = frozen !== null ? () => frozen : () => start + (Date.now() - booted);
}
// Without an account the browser keeps the guest's data itself; the demo's guest keeps it in memory.
const route = parseRoute(window.location.pathname);
const publicPage = route.name === 'privacy' || route.name === 'support';
// Information pages need neither saved guest data nor the account.
const api: Api = publicPage ? new HttpApi() : demo
  ? params.has('guest') ? new GuestApi(clock, timeZone, null)
    : new DemoApi(clock, timeZone, {
      signedIn: !params.has('signedout'), empty: params.has('empty'), unreachable: params.has('unreachable'), free: params.has('free'),
    })
  : guestMode() ? new GuestApi(clock, timeZone) : new HttpApi();

const store = new Store({ api, clock, timeZone });
// Screenshot runs drive the demo through the store (open a sheet, switch a view); never with a real account.
if (demo) (window as unknown as { tallyDemo?: Store }).tallyDemo = store;
const t = translator(lang);
store.setLanguage(lang, t, makeFormats(lang, timeZone, t));
document.documentElement.lang = lang;
if (!publicPage) void store.start();

const root = document.getElementById('root');
if (root) createRoot(root).render(<StoreProvider store={store}>
  {!publicPage && api instanceof HttpApi ? <AuthBoundary api={api}><App /></AuthBoundary> : <App />}
</StoreProvider>);
